import { createAction } from '@reduxjs/toolkit';

import errorTypes from '../../constants/errorTypes';
import { urlUtils } from '../../utils/url';
import { fetchFlightTime } from '../../services/flight';
import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { getTdmTypeFromUrl, isStopOver } from '../../utils/url/url';
import { fetchLabels, fetchDestinationContent } from '../../services/content';
import {
  logCombinationSummary,
  logCruiseSummary,
  logPackagePlusSearch,
} from '../../utils/logUtils/ComboSummaryLogUtils';
import { fetchSummary, addToCart as addCombinationToCart } from '../../services/combination';

// Every one of these is dispatched by a thunk, so none can be slice-generated — the container
// calls only `init`, `getFlightTime`, `addToCart` and `errorPopupToggled`, all of which live here.
export const contentLoaded = createAction('comboSummary/contentLoaded');
export const initSucceeded = createAction('comboSummary/initSucceeded');
export const initFailed = createAction('comboSummary/initFailed');
export const initFinished = createAction('comboSummary/initFinished');
export const messageAdded = createAction('comboSummary/messageAdded');
export const errorPopupToggled = createAction('comboSummary/errorPopupToggled');
export const addToCartStarted = createAction('comboSummary/addToCartStarted');
export const addToCartFailed = createAction('comboSummary/addToCartFailed');
export const addToCartSucceeded = createAction('comboSummary/addToCartSucceeded');
export const flightTimeLoaded = createAction('comboSummary/flightTimeLoaded');

// NB: No reducer reads this one — it is the old bare `INIT`, dispatched and ignored. Kept so the
//     conversion stays a swap rather than quietly removing a store notification. A loading flag
//     could hang off it, or it can go once someone confirms nothing wants it.
export const initStarted = createAction('comboSummary/initStarted');

const GENERIC_ERROR = {
  title: 'An error occurred, please redo your search.',
  severity: 'ERROR',
};

const getLabels = async (siteSettings) => {
  const [labels, flightListLabels] = await Promise.all([
    fetchLabels(siteSettings, 'iwCombinationSummary'),
    fetchLabels(siteSettings, 'iwFlightList'),
  ]);

  const { general, ...rest } = labels;
  return {
    ...rest,
    ...flightListLabels,
    ...general,
  };
};

const getSummary = async (siteSettings, userId) => {
  const result = await fetchSummary(siteSettings, userId);
  return result;
};

const mapPackagePlusData = ({ extras, hotels: hotelsData }) => {
  const nonMainExtras = extras?.filter((e) => e?.mainProduct === false) || [];

  const extrasCategorized = hotelsData.reduce(
    (acc, hotel) => {
      const key = `${hotel.name}_${hotel.accomodationBookingKey}`;
      acc[key] = [];
      return acc;
    },
    { notAssociatedWithAnyHotel: [] },
  );

  nonMainExtras.forEach((e) => {
    const optionFromDate = new Date(e?.presentationDateValue).getTime();
    const matchedHotel = hotelsData.find(({ name, checkIn, checkOut }) => {
      const checkInDate = new Date(checkIn).getTime();
      const checkOutDate = new Date(checkOut).getTime();
      return name === e.hotelName && checkInDate <= optionFromDate && optionFromDate <= checkOutDate;
    });
    if (matchedHotel) {
      const key = `${matchedHotel.name}_${matchedHotel.accomodationBookingKey}`;
      extrasCategorized[key].push(e);
    } else {
      extrasCategorized.notAssociatedWithAnyHotel.push(e);
    }
  });

  const tempHotels = hotelsData.map((hotel) => ({
    ...hotel,
    hotelExtras: extrasCategorized[`${hotel.name}_${hotel.accomodationBookingKey}`] || [],
  }));

  return {
    hotels: tempHotels,
    extrasNotAssociatedWithAnyHotel: extrasCategorized.notAssociatedWithAnyHotel,
  };
};

// The body of the old `initFinished` thunk, kept as a helper taking `dispatch` rather than inlined,
// so `init` below stays scannable. Order of effects is unchanged: the outcome, the translated
// message, logging, the URL write, the session-expiry popup, then `initFinished`.
const applyInitOutcome = (dispatch, { labels, data, isPackagePlus }) => {
  const { selectedPackage, serviceCallInformation, loggingData } = data.result;
  const { sessionHasExpired } = serviceCallInformation;
  const isNewSearch = !urlUtils.getSessionIdFromUrl();

  if (selectedPackage?.hotels) {
    dispatch(
      initSucceeded({
        labels: {
          ...labels,
          productsIncludedParsed: isPackagePlus
            ? labels.productsIncludedPackagePlus
            : getProductsIncluded(selectedPackage?.packageTag, labels),
        },
        selectedCombination: data.result.selectedPackage,
        searchInformation: data.result.searchInformation,
      }),
    );
  } else {
    dispatch(initFailed({ labels }));
  }

  if (data?.translatedMessage) {
    dispatch(messageAdded(data.translatedMessage));
  }

  if (loggingData) {
    if (selectedPackage?.cruise) {
      logCruiseSummary(loggingData, selectedPackage.cruise);
    } else {
      logCombinationSummary(loggingData);
      // GTM builds the plus-package Search event from the params the summary event just pushed.
      if (isPackagePlus) {
        logPackagePlusSearch();
      }
    }
  }

  if (isNewSearch && selectedPackage) {
    urlUtils.updateQueryWithValues({
      SessionId: serviceCallInformation?.sessionId,
      TransportKey: selectedPackage?.transport?.transportKey,
      TransportId: selectedPackage?.transport?.id,
      AccomodationKeys: selectedPackage?.accommodationKeys.join(','),
    });
  }

  if (sessionHasExpired) {
    dispatch(
      errorPopupToggled({
        errorType: errorTypes.SESSION_EXPIRED,
        visible: true,
      }),
    );
  }

  dispatch(initFinished());
};

// Stays a hand-written thunk, per the D1 refinement — this is the clearest case of the five. It
// has four exits: the early translated-message failure that returns before `initFinished`, the
// loaded outcome, the no-hotels outcome, and the catch. `initFinished` fires on three of them, and
// the early exit relies on `initFailed` also setting `initialized` in the slice.
//
// `init` takes `siteSettings` and `userId` as arguments rather than reading `getState().common`,
// which is how the container calls it; left as-is.
export const init = (siteSettings, userId) => async (dispatch) => {
  const destinationContent = await fetchDestinationContent(siteSettings);

  dispatch(contentLoaded(destinationContent));
  dispatch(initStarted());

  try {
    const [labels, summaryResult] = await Promise.all([getLabels(siteSettings), getSummary(siteSettings, userId)]);

    if (summaryResult.statusCode !== 200) {
      dispatch(
        messageAdded({
          title: labels?.iwErrors?.systemError?.heading || GENERIC_ERROR.title,
          severity: 'ERROR',
        }),
      );
    }

    if (summaryResult.translatedMessage && !summaryResult.result.selectedPackage) {
      dispatch(messageAdded(summaryResult.translatedMessage));
      dispatch(initFailed({ labels }));
      return;
    }

    const tdmType = getTdmTypeFromUrl();
    const isPackagePlus = tdmType?.toLowerCase() === 'pluspackage';

    if (isPackagePlus) {
      const { hotels, extrasNotAssociatedWithAnyHotel } = mapPackagePlusData(summaryResult.result.selectedPackage);
      summaryResult.result.selectedPackage = {
        ...summaryResult.result.selectedPackage,
        hotels,
        extrasNotAssociatedWithAnyHotel,
      };
    }

    if (isStopOver() && tdmType === '') {
      const fetchedHotelId = summaryResult.result.selectedPackage?.hotels.map((hotel) => hotel.id);
      const { HotelIds } = siteSettings;
      const hotelIdsMatched = HotelIds.every((id) => fetchedHotelId.includes(parseInt(id, 10)));

      if (!hotelIdsMatched) {
        dispatch(
          messageAdded({
            title: labels.stopOverHotelMismatchText,
            severity: 'information',
          }),
        );
      }
    }

    applyInitOutcome(dispatch, { labels, data: summaryResult, isPackagePlus });
  } catch (_error) {
    dispatch(initFinished());
    dispatch(messageAdded(GENERIC_ERROR));
  }
};

export const addToCart = (flightRouteKeys, cruiseKey, link) => async (dispatch, getState) => {
  dispatch(addToCartStarted());

  const { common } = getState();
  const { siteSettings, userId } = common;

  const addToCartResponse = await addCombinationToCart(siteSettings, userId, flightRouteKeys, cruiseKey);
  const { result } = addToCartResponse;

  if (!result.success) {
    dispatch(addToCartFailed());

    if (result.sessionHasExpired) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SESSION_EXPIRED,
          visible: true,
        }),
      );
      return;
    }

    if (addToCartResponse.translatedMessage) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SYSTEM_ERROR_TRANSLATED,
          visible: true,
          title: addToCartResponse.translatedMessage.title,
          message: addToCartResponse.translatedMessage.message,
        }),
      );
      return;
    }

    if (addToCartResponse.errorMessage) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SYSTEM_ERROR,
          visible: true,
          message: addToCartResponse.errorMessage,
        }),
      );
    }

    return;
  }

  dispatch(addToCartSucceeded());

  if (!link.startsWith('/')) {
    link = `/${link}`;
  }

  window.location = `${link}`;
};

export const getFlightTime = (homeboundTransportKey, outboundTransportKey) => async (dispatch, getState) => {
  const { siteSettings } = getState().common;
  const sessionId = urlUtils.getSessionIdFromUrl();

  const response = await fetchFlightTime(sessionId, homeboundTransportKey, outboundTransportKey, siteSettings);

  dispatch(flightTimeLoaded(response?.result));
};
